import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Mail, Send, FileText, Plus, ArrowRight, Loader2, Database, CheckCircle2, Clock, LayoutDashboard, Shield, ExternalLink } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { campaignsApi, signupFormsApi, teamsApi, usersApi, CampaignRecord, SignupFormRecord, TeamRecord, UserRecord } from '../services/creatorApi';
import { crmMailingListsApi, CrmMailingList } from '../services/crmApi';
import { toast } from 'sonner';
export function Dashboard() {
  const {
    currentWorkspace,
    currentUser,
    currentSubRegion
  } = useWorkspace();
  const [loading, setLoading] = useState(true);
  // Data state
  const [stats, setStats] = useState({
    contacts: 0,
    lists: 0,
    campaigns: 0,
    forms: 0
  });
  const [recentCampaigns, setRecentCampaigns] = useState<CampaignRecord[]>([]);
  const [recentLists, setRecentLists] = useState<CrmMailingList[]>([]);
  // Super Admin Data
  const [allTeams, setAllTeams] = useState<TeamRecord[]>([]);
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch data in parallel
        const [campaignsResult, formsResult, listsData, teamsResult, usersResult] = await Promise.all([campaignsApi.list(), signupFormsApi.list(), crmMailingListsApi.list(), currentUser.Role === 'Super Admin' ? teamsApi.list() : Promise.resolve([]), currentUser.Role === 'Super Admin' ? usersApi.list() : Promise.resolve([])]);
        const campaignsData = campaignsResult;
        const formsData = formsResult;
        const teamsData = teamsResult;
        const usersData = usersResult;
        // Filter by current workspace/team
        const teamCampaigns = campaignsData.filter(c => c.Team === currentWorkspace.name && (!currentSubRegion || !c.Region || c.Region === currentSubRegion));
        const teamForms = formsData.filter(f => f.Team === currentWorkspace.name && (!currentSubRegion || !f.Region || f.Region === currentSubRegion));
        const teamLists = listsData.filter(l => l.Team === currentWorkspace.name && (!currentSubRegion || !l.Region || l.Region === currentSubRegion));
        // Sort campaigns by date (newest first)
        const sortedCampaigns = [...teamCampaigns].sort((a, b) => {
          return (b.Exported_At || b.ID).localeCompare(a.Exported_At || a.ID);
        }).slice(0, 5);
        setRecentCampaigns(sortedCampaigns);
        setRecentLists(teamLists.slice(0, 5));
        // Use mailing list subscriber counts as contact count proxy
        // (avoids slow listAll() that pages through every contact)
        const contactCount = teamLists.reduce((acc, list) => acc + (list.Subscriber_Count || 0), 0);
        setStats({
          contacts: contactCount,
          lists: teamLists.length,
          campaigns: teamCampaigns.length,
          forms: teamForms.length
        });
        if (currentUser.Role === 'Super Admin') {
          setAllTeams(teamsData);
          setAllUsers(usersData);
        }
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [currentWorkspace.name, currentSubRegion, currentUser.Role]);
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Sent':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-3672">
            <CheckCircle2 className="h-3 w-3" data-id="element-3673" />
            Sent
          </span>;
      case 'Exported':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-3674">
            <Database className="h-3 w-3" data-id="element-3675" />
            Exported
          </span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-3676">
            <FileText className="h-3 w-3" data-id="element-3677" />
            Draft
          </span>;
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]" data-id="element-3678">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3679" />
      </div>;
  }
  return <div className="space-y-8 pb-12 animate-in fade-in duration-500" data-id="element-3680">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3681">
        <div data-id="element-3682">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-3683">
            Dashboard
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-3684">
            Welcome back, {currentUser.User_Name}. Here's what's happening in{' '}
            {currentWorkspace.name}.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-3685">
          <span className="text-sm text-gray-500 bg-white px-3 py-1.5 rounded-md border border-gray-200 shadow-sm flex items-center gap-2" data-id="element-3686">
            <Clock className="h-4 w-4" data-id="element-3687" />
            {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3688">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-3689">
          <div className="flex items-center justify-between" data-id="element-3690">
            <div data-id="element-3691">
              <p className="text-sm font-medium text-gray-500" data-id="element-3692">
                Total Contacts
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3693">
                {stats.contacts.toLocaleString()}
              </p>
            </div>
            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-3694">
              <Users className="h-5 w-5 text-blue-600" data-id="element-3695" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-3696">
            <Link to="/contacts" className="flex items-center gap-1" data-id="element-3697">
              View all contacts <ArrowRight className="h-4 w-4" data-id="element-3698" />
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-3699">
          <div className="flex items-center justify-between" data-id="element-3700">
            <div data-id="element-3701">
              <p className="text-sm font-medium text-gray-500" data-id="element-3702">Mailing Lists</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3703">
                {stats.lists}
              </p>
            </div>
            <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-3704">
              <Mail className="h-5 w-5 text-purple-600" data-id="element-3705" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-purple-600 hover:text-purple-700 font-medium" data-id="element-3706">
            <Link to="/mailing-lists" className="flex items-center gap-1" data-id="element-3707">
              Manage lists <ArrowRight className="h-4 w-4" data-id="element-3708" />
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-3709">
          <div className="flex items-center justify-between" data-id="element-3710">
            <div data-id="element-3711">
              <p className="text-sm font-medium text-gray-500" data-id="element-3712">Campaigns</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3713">
                {stats.campaigns}
              </p>
            </div>
            <div className="h-10 w-10 bg-green-50 rounded-lg flex items-center justify-center" data-id="element-3714">
              <Send className="h-5 w-5 text-green-600" data-id="element-3715" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-green-600 hover:text-green-700 font-medium" data-id="element-3716">
            <Link to="/campaigns" className="flex items-center gap-1" data-id="element-3717">
              View campaigns <ArrowRight className="h-4 w-4" data-id="element-3718" />
            </Link>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-3719">
          <div className="flex items-center justify-between" data-id="element-3720">
            <div data-id="element-3721">
              <p className="text-sm font-medium text-gray-500" data-id="element-3722">Signup Forms</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3723">
                {stats.forms}
              </p>
            </div>
            <div className="h-10 w-10 bg-orange-50 rounded-lg flex items-center justify-center" data-id="element-3724">
              <FileText className="h-5 w-5 text-orange-600" data-id="element-3725" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-orange-600 hover:text-orange-700 font-medium" data-id="element-3726">
            <Link to="/forms" className="flex items-center gap-1" data-id="element-3727">
              Edit forms <ArrowRight className="h-4 w-4" data-id="element-3728" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-3729">
        {/* Recent Campaigns - 2/3 width */}
        <div className="lg:col-span-2 space-y-6" data-id="element-3730">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3731">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-3732">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-3733">
                Recent Campaigns
              </h2>
              <Link to="/campaigns" className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-3734">
                View All
              </Link>
            </div>
            <div className="overflow-x-auto" data-id="element-3735">
              {recentCampaigns.length > 0 ? <table className="min-w-full divide-y divide-gray-200" data-id="element-3736">
                  <thead className="bg-gray-50" data-id="element-3737">
                    <tr data-id="element-3738">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3739">
                        Campaign Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3740">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3741">
                        Recipients
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3742">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-3743">
                    {recentCampaigns.map(campaign => <tr key={campaign.ID} className="hover:bg-gray-50 transition-colors" data-id="element-3744">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3745">
                          <div className="text-sm font-medium text-gray-900" data-id="element-3746">
                            {campaign.Campaign_Name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-3747">
                            {campaign.Email_Subject}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-3748">
                          {getStatusBadge(campaign.Status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600" data-id="element-3749">
                          {campaign.Recipient_Count?.toLocaleString() || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3750">
                          {formatDate(campaign.Exported_At)}
                        </td>
                      </tr>)}
                  </tbody>
                </table> : <div className="p-8 text-center text-gray-500" data-id="element-3751">
                  No campaigns found.{' '}
                  <Link to="/campaigns/new" className="text-blue-600 hover:underline" data-id="element-3752">
                    Create your first campaign
                  </Link>
                </div>}
            </div>
          </div>

          {/* Super Admin Team Overview */}
          {currentUser.Role === 'Super Admin' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3753">
              <div className="p-6 border-b border-gray-200 flex items-center gap-2" data-id="element-3754">
                <Shield className="h-5 w-5 text-purple-600" data-id="element-3755" />
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-3756">
                  Team Overview (Super Admin)
                </h2>
              </div>
              <div className="overflow-x-auto" data-id="element-3757">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3758">
                  <thead className="bg-gray-50" data-id="element-3759">
                    <tr data-id="element-3760">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3761">
                        Team Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3762">
                        Region
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3763">
                        Users
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3764">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-3765">
                    {allTeams.map(team => {
                  const teamUsers = allUsers.filter(u => u.Team === team.Team_Name);
                  return <tr key={team.ID} className="hover:bg-gray-50 transition-colors" data-id="element-3766">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3767">
                            <div className="text-sm font-medium text-gray-900" data-id="element-3768">
                              {team.Team_Name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3769">
                            {team.Team_Regions?.map(r => r.Region_Name).join(', ') || 'All Regions'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3770">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-3771">
                              {teamUsers.length} users
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-3772">
                            <Link to="/users" className="text-blue-600 hover:text-blue-900" data-id="element-3773">
                              Manage
                            </Link>
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>

        {/* Sidebar / Quick Actions - 1/3 width */}
        <div className="space-y-6" data-id="element-3774">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-3775">
            <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-3776">
              Quick Actions
            </h3>
            <div className="space-y-3" data-id="element-3777">
              <Link to="/campaigns/new" className="flex items-center justify-between w-full p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors group" data-id="element-3778">
                <span className="flex items-center gap-3 font-medium" data-id="element-3779">
                  <Send className="h-5 w-5" data-id="element-3780" />
                  Create Campaign
                </span>
                <Plus className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-3781" />
              </Link>

              <Link to="/forms/new" className="flex items-center justify-between w-full p-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors group" data-id="element-3782">
                <span className="flex items-center gap-3 font-medium" data-id="element-3783">
                  <FileText className="h-5 w-5" data-id="element-3784" />
                  New Signup Form
                </span>
                <Plus className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-3785" />
              </Link>

              <Link to="/contacts" className="flex items-center justify-between w-full p-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors group" data-id="element-3786">
                <span className="flex items-center gap-3 font-medium" data-id="element-3787">
                  <Users className="h-5 w-5" data-id="element-3788" />
                  View Contacts
                </span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-3789" />
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-3790">
            <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-3791">
              Active Mailing Lists
            </h3>
            <div className="space-y-4" data-id="element-3792">
              {recentLists.length > 0 ? recentLists.map(list => <div key={list.id} className="flex items-center justify-between pb-3 border-b border-gray-100 last:border-0 last:pb-0" data-id="element-3793">
                    <div className="min-w-0" data-id="element-3794">
                      <p className="text-sm font-medium text-gray-900 truncate" data-id="element-3795">
                        {list.Name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-3796">
                        {list.Subscriber_Count || 0} subscribers
                      </p>
                    </div>
                    <Link to={`/mailing-lists/${list.id}`} className="text-gray-400 hover:text-blue-600" data-id="element-3797">
                      <ExternalLink className="h-4 w-4" data-id="element-3798" />
                    </Link>
                  </div>) : <p className="text-sm text-gray-500" data-id="element-3799">No mailing lists found.</p>}
              <Link to="/mailing-lists" className="block text-center text-sm text-blue-600 hover:text-blue-700 font-medium pt-2" data-id="element-3800">
                View All Lists
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>;
}